import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'redux/store';
import { ProjectResponse } from 'types/entity/project-response';
import {
  ProjectCommonModelProjectStatusCodeEnum,
  ProjectResponseAllOfAdminFragEnum,
  ProjectResponseAllOfTemplateFragEnum,
} from 'types/entity';
import { isOpenOrFrozenOrClosed } from 'utils/service-project-status';
import { serviceProjectStatusFilter } from 'utils/service-project-filter';
import { ServiceProjectStatusResponseServiceProjectStatusEnum } from 'types/entity/service-project-status-response';
import { selectAllProject, selectProjectById } from './project-read.adapter';
import { ProjectReadState } from './project-read.slice';
import { initialProjectValue } from '../initial-state';

const selectSelf = (state: RootState) => state;
const getProjectState = createSelector(selectSelf, (state) => state.projectRead);

const getLoaded = createSelector(
  getProjectState,
  (state: ProjectReadState) => state.loaded,
);

const getProjectListLoaded = createSelector(
  getProjectState,
  (state: ProjectReadState) => state.projectListLoaded,
);

const getEntities = createSelector(
  selectSelf,
  selectAllProject,
);

const getSelectedEntity = createSelector(
  selectSelf,
  (state: RootState) => selectProjectById(state, state.projectRead.selectedId) || initialProjectValue,
);

const getSelectedProjectId = createSelector(
  getProjectState,
  (state: ProjectReadState) => state.selectedId,
);

const getSelectedProjectTemplateFrag = createSelector(
  getSelectedEntity,
  (entity: ProjectResponse) => entity?.template_frag === ProjectResponseAllOfTemplateFragEnum.Template,
);

const getSelectedProjectAdminFrag = createSelector(
  getSelectedEntity,
  (entity: ProjectResponse) => entity?.admin_frag === ProjectResponseAllOfAdminFragEnum.Admin,
);

const isFpReadableProject = createSelector(
  getSelectedEntity,
  (entity: ProjectResponse) => isOpenOrFrozenOrClosed(serviceProjectStatusFilter('FILE_PORTAL', entity.service_project_statuses || []) || ServiceProjectStatusResponseServiceProjectStatusEnum.Delete),
);

const isFpEditableProject = createSelector(
  getSelectedEntity,
  (entity: ProjectResponse) => serviceProjectStatusFilter('FILE_PORTAL', entity.service_project_statuses || []) === ServiceProjectStatusResponseServiceProjectStatusEnum.Open,
);

const isEditableExtendedAttributeProject = createSelector(
  getSelectedEntity,
  (entity: ProjectResponse) => entity?.project_status_code === ProjectCommonModelProjectStatusCodeEnum.Ps10
            && serviceProjectStatusFilter('FILE_PORTAL', entity.service_project_statuses || []) === ServiceProjectStatusResponseServiceProjectStatusEnum.Open,
);

const isDdmsReadableProject = createSelector(
  getSelectedEntity,
  (entity: ProjectResponse) => isOpenOrFrozenOrClosed(serviceProjectStatusFilter('DOC_MGMT', entity?.service_project_statuses || []) || ServiceProjectStatusResponseServiceProjectStatusEnum.Delete),
);

const isPhotoReadableProject = createSelector(
  getSelectedEntity,
  (entity: ProjectResponse) => isOpenOrFrozenOrClosed(serviceProjectStatusFilter('PHOTORUCTION', entity.service_project_statuses || []) || ServiceProjectStatusResponseServiceProjectStatusEnum.Delete),
);

const getServiceProjectPolicies = createSelector(
  getSelectedEntity,
  (entity: ProjectResponse) => entity.service_project_policies || [],
);

export const projectReadQuery = {
  getLoaded,
  getProjectListLoaded,
  getEntities,
  getSelectedEntity,
  getSelectedProjectId,
  getSelectedProjectTemplateFrag,
  getSelectedProjectAdminFrag,
  isFpReadableProject,
  isFpEditableProject,
  isEditableExtendedAttributeProject,
  isDdmsReadableProject,
  isPhotoReadableProject,
  getServiceProjectPolicies,
};
